import React, { Fragment, useMemo, memo } from 'react'
import { useSelector } from 'react-redux'
import {
  getHangerState,
  getJobSettingsState,
  getSettingsState,
  selectAll,
} from '../../../store/selectors'

import {
  countryOptions as countryData,
  connectionOptions as connectionData,
  hanger as hangerTypeData,
  downloadDurationTypeOptions,
  upliftLoadDurationTypeOptions,
  hanger as hangerData,
} from '../../../common/json/common.json'
import {
  header as headerData,
  girder as girderData,
} from '../../../common/json/carrying-member.json'
import { joist as joistData, truss as trussData } from '../../../common/json/carried-member.json'

import { getNestedObject } from '../../../common/fns'
import { RenderIf } from '../../../../../utils/fns'

import Output from '../../../views/Output'
import { PrintTable } from '.'
import { JobTally } from '../../../views/Output/components'
import { getCalcInputState } from '../../../../../utils/selectors'

const getText = (list, value) => {
  const target = list.find((item) => item.value === value)
  if (target) {
    return target.text
  } else {
    return 'Not Applicable'
  }
}

const getMaterialText = (list, type, country, value) => {
  if (list[type] && list[type][country]) {
    return getText(list[type][country], value)
  } else if (list[type]) {
    return getText(list[type], value)
  } else {
    return 'Not Applicable'
  }
}

const isArray = (data) => Array.isArray(data)

const PrintContent = () => {
  const jobSettings = useSelector(getJobSettingsState)
  const setting = useSelector(getSettingsState)
  const { country, connectionType } = setting
  const hangerOptions = useSelector(getHangerState)
  const { header, girder, joist, truss, jack, leftHip, rightHip } = useSelector(getCalcInputState)

  const jobs = useSelector(selectAll)

  const memberColumns = (member, dataset, name) => {
    const { type, material, width, isRough } = member

    const widthHeader = {
      joist: 'Width',
      header: 'Width',
    }
    const heightHeader = {
      joist: 'Depth',
      header: 'Depth',
      girder: 'Bottom Chord Height',
    }

    const getOptionsByKey = (opts, ...keys) => (isArray(opts) ? opts : getNestedObject(opts, keys))

    const widthOptions = () => {
      const data = dataset.widthData.find(
        (item) => item.type === type && item.material.includes(material),
      )

      return data ? getOptionsByKey(data.options, [isRough ? 'rough' : 'default']) : []
    }

    const depthOptions = () => {
      const data = dataset.depthData.find(
        (item) =>
          item.type === type &&
          item.material.includes(material) &&
          // TODO: Need to have dynamic condition for dependent data
          (item.width || [width]).includes(width),
      )
      return data ? getOptionsByKey(data.options, [isRough ? 'rough' : 'default']) : []
    }

    return [
      {
        Header: 'Member Type',
        accessor: 'type',
        // eslint-disable-next-line react/prop-types
        Cell: ({ value }) => <span>{getText(dataset.memberTypeData, value)}</span>,
      },
      {
        Header: 'Lumber Species',
        accessor: 'material',
        // eslint-disable-next-line react/prop-types
        Cell: ({ value }) => (
          <span>{getMaterialText(dataset.materialData, type, country, value)}</span>
        ),
      },
      {
        Header: widthHeader[name] ? widthHeader[name] : 'Bottom Chord Width',
        accessor: 'width',
        // eslint-disable-next-line react/prop-types
        Cell: ({ value }) => <span>{getText(widthOptions(), value)}</span>,
      },
      {
        Header: heightHeader[name] ? heightHeader[name] : 'Heel Height',
        accessor: 'depth',
        // eslint-disable-next-line react/prop-types
        Cell: ({ value }) =>
          heightHeader[name] ? <span>{getText(depthOptions(), value)}</span> : <span>{value}</span>,
      },
      {
        Header: 'Number of Plies',
        accessor: 'ply',
      },
      {
        Header: 'Member ID',
        accessor: 'memberID',
      },
    ]
  }

  const settingColumns = useMemo(
    () => [
      {
        Header: 'Country',
        accessor: 'country',
        // eslint-disable-next-line react/prop-types
        Cell: ({ value }) => <span>{getText(countryData, value)}</span>,
      },
      {
        Header: 'Connection Type',
        accessor: 'connectionType',
        // eslint-disable-next-line react/prop-types
        Cell: ({ value }) => <span>{getText(connectionData, value)}</span>,
      },
    ],
    [],
  )

  const jobSettingsColumns = useMemo(
    () => [
      {
        Header: 'Hanger Type',
        accessor: 'hangerType',
        // eslint-disable-next-line react/prop-types
        Cell: ({ value }) => <span>{getText(hangerTypeData.options, value)}</span>,
      },
      {
        Header: 'Download Duration',
        accessor: 'designInformations.downloadDurationType',
        // eslint-disable-next-line react/prop-types
        Cell: ({ value }) => <span>{getText(downloadDurationTypeOptions[country], value)}</span>,
      },
      {
        Header: 'Uplift Duration',
        accessor: 'designInformations.upliftLoadDurationType',
        // eslint-disable-next-line react/prop-types
        Cell: ({ value }) => <span>{getText(upliftLoadDurationTypeOptions[country], value)}</span>,
      },
      {
        Header: 'Job ID',
        accessor: 'jobID',
      },
      {
        Header: 'Quantity',
        accessor: 'quantity',
      },
    ],
    [country],
  )

  const isRoughColumn = useMemo(
    () => [
      {
        Header: 'Rough Lumber',
        accessor: 'isRough',
        // eslint-disable-next-line react/prop-types
        Cell: ({ value }) => <span>{value ? 'Yes' : 'No'}</span>,
      },
    ],
    [],
  )

  const loadsColumns = useMemo(
    () => [
      {
        Header: 'Download (ASD)',
        accessor: 'loads.load',
      },
      {
        Header: 'Uplift (ASD)',
        accessor: 'loads.uplift',
      },
    ],
    [],
  )

  const angleColumns = useMemo(
    () => [
      {
        Header: 'Skew Type',
        accessor: 'angle.skewType',
        // eslint-disable-next-line react/prop-types
        Cell: ({ value }) => <span>{getText(hangerData.skewType.options, value)}</span>,
      },
      {
        Header: 'Skew (Degrees)',
        accessor: 'angle.skewAngle',
      },
      {
        Header: 'Slope Type',
        accessor: 'angle.slopeType',
        // eslint-disable-next-line react/prop-types
        Cell: ({ value }) => <span>{getText(hangerData.slopeType.options, value)}</span>,
      },
      {
        Header: 'Slope (Degrees)',
        accessor: 'angle.slopeAngle',
      },
    ],
    [],
  )

  const hangerOptionsColumns = useMemo(() => {
    return [
      {
        Header: 'Skew Type',
        accessor: 'angle.skewType',
        // eslint-disable-next-line react/prop-types
        Cell: ({ value }) => <span>{getText(hangerData.skewType.options, value)}</span>,
      },
      {
        Header: 'Skew (Degrees)',
        accessor: 'angle.skewAngle',
      },
      {
        Header: 'Slope Type',
        accessor: 'angle.slopeType',
        // eslint-disable-next-line react/prop-types
        Cell: ({ value }) => <span>{getText(hangerData.slopeType.options, value)}</span>,
      },
      {
        Header: 'Slope (Degrees)',
        accessor: 'angle.slopeAngle',
      },
      {
        Header: 'Open Closed Type',
        accessor: 'topFlangeOptions.topFlangeOpenClosedType',
        // eslint-disable-next-line react/prop-types
        Cell: ({ value }) => <span>{getText(hangerData.bendType.options, value)}</span>,
      },
      {
        Header: 'Top Flange Bend (Degrees)',
        accessor: 'topFlangeOptions.topFlangeOpenClosedAngle',
      },
      {
        Header: 'Sloped Down Type',
        accessor: 'topFlangeOptions.topFlangeSlopedDownType',
        // eslint-disable-next-line react/prop-types
        Cell: ({ value }) => <span>{getText(hangerData.slopeDownType.options, value)}</span>,
      },
      {
        Header: 'Top Flange Slope (Degrees)',
        accessor: 'topFlangeOptions.topFlangeSlopedDownAngle',
      },
      {
        Header: 'Offset Direction',
        accessor: 'topFlangeOptions.topFlangeOffset',
        // eslint-disable-next-line react/prop-types
        Cell: ({ value }) => <span>{getText(hangerData.offsetType.options, value)}</span>,
      },
      {
        Header: 'High, Low, Center Flush',
        accessor: 'topFlangeOptions.hlcFlush',
        // eslint-disable-next-line react/prop-types
        Cell: ({ value }) => <span>{getText(hangerData.hlcFlush.options, value)}</span>,
      },
    ]
  }, [hangerOptions])

  const headerColumns = useMemo(() => {
    return memberColumns(header, headerData, 'header').concat(isRoughColumn)
  }, [header])

  const joistColumns = useMemo(() => {
    return memberColumns(joist, joistData, 'joist').concat(isRoughColumn).concat(loadsColumns)
  }, [joist])

  const girderColumns = useMemo(() => {
    return memberColumns(girder, girderData, 'girder').concat([
      {
        Header: 'Top Chord Ply',
        accessor: 'topChordPly',
      },
      {
        Header: 'Vertical Width (King Post)',
        accessor: 'kingWidth',
      },
      {
        Header: 'Total Height',
        accessor: 'kingHeight',
      },
    ])
  }, [girder])

  const trussColumns = useMemo(() => {
    return memberColumns(truss, trussData, 'truss').concat(loadsColumns)
  }, [truss])

  const jackColumns = useMemo(() => {
    return memberColumns(jack, trussData, 'jack')
      .concat(loadsColumns)
      .concat([
        {
          Header: 'Slope Type',
          accessor: 'angle.slopeType',
          // eslint-disable-next-line react/prop-types
          Cell: ({ value }) => <span>{getText(hangerData.slopeType.options, value)}</span>,
        },
        {
          Header: 'Slope Angle',
          accessor: 'angle.slopeAngle',
        },
      ])
  }, [jack])

  const leftHipColumns = useMemo(() => {
    return memberColumns(leftHip, trussData, 'leftHip').concat(loadsColumns).concat(angleColumns)
  }, [leftHip])

  const rightHipColumns = useMemo(() => {
    return memberColumns(rightHip, trussData, 'rightHip')
      .concat(loadsColumns)
      .concat(angleColumns)
      .concat([
        {
          Header: 'isDuplicate',
          accessor: 'isDuplicate',
          // eslint-disable-next-line react/prop-types
          Cell: ({ value }) => <span>{value ? 'Yes' : 'No'}</span>,
        },
      ])
  }, [rightHip])

  return (
    <div className="space-y-6">
      {RenderIf(
        jobs.length > 0,
        <div className="space-y-2">
          <div>
            <h3>Job List</h3>
            <JobTally pdfFormat={true} />
          </div>
          <div className="page-break"></div>
        </div>,
      )}

      {RenderIf(
        jobs.length > 0,
        jobs.map((item, index) => (
          <div key={index}>
            <div className="space-y-2">
              <div className="d-flex" style={{ justifyContent: 'center' }}>
                <div
                  style={{
                    flexGrow: 1,
                  }}
                >
                  <div>
                    <h2>{`${item.jobInputs.jobSettings.jobID} inputs`}</h2>
                    <h2>Input</h2>
                    <div className="mt-4">
                      <h3>Settings</h3>
                      <PrintTable
                        columns={settingColumns}
                        data={[item.jobInputs.settings]}
                        pdfFormat={true}
                      ></PrintTable>
                    </div>
                  </div>
                  <div className="mt-6">
                    <h3>Job Settings</h3>
                    <PrintTable
                      columns={jobSettingsColumns}
                      data={[item.jobInputs.jobSettings]}
                      pdfFormat={true}
                    ></PrintTable>
                  </div>
                </div>

                <div className="pl-5">
                  <h2>3D Model</h2>
                  <div className="model-screenshot">
                    <img src={item.config3d.imageUrl} alt="hs-screenshot" />
                  </div>
                </div>
              </div>

              {RenderIf(
                item.jobInputs.settings.connectionType === 'joist',
                <div>
                  <h3>Header</h3>
                  <PrintTable
                    columns={headerColumns}
                    data={[item.jobInputs.header]}
                    pdfFormat={true}
                  ></PrintTable>
                  <div className="mt-6">
                    <h3>Joist</h3>
                    <PrintTable
                      columns={joistColumns}
                      data={[item.jobInputs.joist]}
                      pdfFormat={true}
                    ></PrintTable>
                  </div>
                  <div className="mt-6">
                    <h3>Hanger Options</h3>
                    <PrintTable
                      columns={hangerOptionsColumns}
                      data={[item.jobInputs.hanger]}
                      pdfFormat={true}
                    ></PrintTable>
                  </div>
                  <div className="page-break"></div>
                </div>,
              )}

              {RenderIf(
                item.jobInputs.settings.connectionType === 'truss',
                <div>
                  <h3>Girder</h3>
                  <PrintTable
                    columns={girderColumns}
                    data={[item.jobInputs.girder]}
                    pdfFormat={true}
                  ></PrintTable>

                  <div className="mt-6">
                    <h3>Truss</h3>
                    <PrintTable
                      columns={trussColumns}
                      data={[item.jobInputs.truss]}
                      pdfFormat={true}
                    ></PrintTable>
                  </div>
                  <div className="mt-6">
                    <h3>Hanger Options</h3>
                    <PrintTable
                      columns={hangerOptionsColumns}
                      data={[item.jobInputs.hanger]}
                      pdfFormat={true}
                    ></PrintTable>
                  </div>
                  <div className="page-break"></div>
                </div>,
              )}

              {RenderIf(
                item.jobInputs.settings.connectionType === 'multiTruss',
                <div>
                  <h3>Girder</h3>
                  <PrintTable
                    columns={girderColumns}
                    data={[item.jobInputs.girder]}
                    pdfFormat={true}
                  ></PrintTable>
                  {RenderIf(
                    item.jobInputs.jobSettings.configuration !== 'leftRight',
                    <div className="mt-6">
                      <h3>Jack</h3>
                      <PrintTable
                        columns={jackColumns}
                        data={[item.jobInputs.jack]}
                        pdfFormat={true}
                      ></PrintTable>
                    </div>,
                  )}
                  {RenderIf(
                    item.jobInputs.jobSettings.configuration !== 'centerRight',
                    <div className="mt-6">
                      <h3>Left Hip</h3>
                      <PrintTable
                        columns={leftHipColumns}
                        data={[item.jobInputs.leftHip]}
                        pdfFormat={true}
                      ></PrintTable>
                    </div>,
                  )}
                  {RenderIf(
                    item.jobInputs.jobSettings.configuration !== 'leftCenter',
                    <div className="mt-6">
                      <h3>Right Hip</h3>
                      <PrintTable
                        columns={rightHipColumns}
                        data={[item.jobInputs.rightHip]}
                        pdfFormat={true}
                      ></PrintTable>
                    </div>,
                  )}
                  <div className="page-break"></div>
                </div>,
              )}

              {/* <div className="page-footer">{`${item.jobInputs.jobSettings.jobID} inputs`}</div> */}
            </div>
            <div className="space-y-2">
              <div>
                <h2>{`${item.jobInputs.jobSettings.jobID} output`}</h2>
                <h2>Output</h2>
                <div className="mt-4">
                  <h3>Result</h3>
                  <Output pdfFormat={true} jobList={jobs} jobItem={item} />
                </div>
                {/* <div className="page-footer">{`${item.jobInputs.jobSettings.jobID} output`}</div> */}
                <div className="page-break"></div>
              </div>
            </div>
          </div>
        )),
      )}

      {RenderIf(
        jobs.length == 0,
        <>
          <div className="space-y-2">
            <div className="d-flex" style={{ justifyContent: 'center' }}>
              <div
                style={{
                  flexGrow: 1,
                }}
              >
                <div>
                  <h2>Input</h2>
                  <div className="mt-4">
                    <h3>Settings</h3>
                    <PrintTable
                      columns={settingColumns}
                      data={[setting]}
                      pdfFormat={true}
                    ></PrintTable>
                  </div>
                </div>
                <div className="mt-6">
                  <h3>Job Settings</h3>
                  <PrintTable
                    columns={jobSettingsColumns}
                    data={[jobSettings]}
                    pdfFormat={true}
                  ></PrintTable>
                </div>
              </div>

              <div className="pl-5">
                <h2>3D Model</h2>
                <div className="model-screenshot">
                  <img src={null} alt="hs-screenshot" />
                </div>
              </div>
            </div>

            {RenderIf(
              connectionType === 'joist',
              <div>
                <h3>Header</h3>
                <PrintTable columns={headerColumns} data={[header]} pdfFormat={true}></PrintTable>
                <div className="mt-6">
                  <h3>Joist</h3>
                  <PrintTable columns={joistColumns} data={[joist]} pdfFormat={true}></PrintTable>
                </div>
                <div className="mt-6">
                  <h3>Hanger Options</h3>
                  <PrintTable
                    columns={hangerOptionsColumns}
                    data={[hangerOptions]}
                    pdfFormat={true}
                  ></PrintTable>
                </div>
              </div>,
            )}

            {RenderIf(
              connectionType === 'truss',
              <div>
                <h3>Girder</h3>
                <PrintTable columns={girderColumns} data={[girder]} pdfFormat={true}></PrintTable>

                <div className="mt-6">
                  <h3>Truss</h3>
                  <PrintTable columns={trussColumns} data={[truss]} pdfFormat={true}></PrintTable>
                </div>
                <div className="mt-6">
                  <h3>Hanger Options</h3>
                  <PrintTable
                    columns={hangerOptionsColumns}
                    data={[hangerOptions]}
                    pdfFormat={true}
                  ></PrintTable>
                </div>
              </div>,
            )}

            {RenderIf(
              connectionType === 'multiTruss',
              <div>
                <h3>Girder</h3>
                <PrintTable columns={girderColumns} data={[girder]} pdfFormat={true}></PrintTable>

                {RenderIf(
                  jobSettings.configuration !== 'leftRight',
                  <div className="mt-6">
                    <h3>Jack</h3>
                    <PrintTable columns={jackColumns} data={[jack]} pdfFormat={true}></PrintTable>
                  </div>,
                )}
                {RenderIf(
                  jobSettings.configuration !== 'centerRight',
                  <div className="mt-6">
                    <h3>Left Hip</h3>
                    <PrintTable
                      columns={leftHipColumns}
                      data={[leftHip]}
                      pdfFormat={true}
                    ></PrintTable>
                  </div>,
                )}
                {RenderIf(
                  jobSettings.configuration !== 'leftCenter',
                  <div className="mt-6">
                    <h3>Right Hip</h3>
                    <PrintTable
                      columns={rightHipColumns}
                      data={[rightHip]}
                      pdfFormat={true}
                    ></PrintTable>
                  </div>,
                )}
              </div>,
            )}
          </div>
          <div className="page-break"></div>

          <div className="space-y-2">
            <div>
              <h2>Output</h2>
              <div className="mt-4">
                <h3>Result</h3>
                <Output pdfFormat={true} />
              </div>
              <div className="page-break"></div>
            </div>
          </div>
        </>,
      )}
    </div>
  )
}

export default memo(PrintContent)
